import moment from 'moment';
import { Col, CommunicationHubEntityType, Row, Typography } from 'components';
import React, { useCallback, useState } from 'react';
import { CalendarIcon } from 'components/common/icons';
import { Note as NoteType } from 'models/ComunicationHub';
import { AttachmentDrawer } from './../attachmentDrawer';
import { NoteAddUpdateModal } from './../addEdit';
import { EntityDrawer } from './../entityDrawer';
import { DeleteModal } from './../delete';
import { Note } from './../note';

interface NoteGroupProps {
  entity: CommunicationHubEntityType;
  groupName: string;
  notes: NoteType[];
  isFinalStatus?: boolean;
  hasAccessToWrite: boolean;
  hasAccessToOtherNoteDelete: boolean;
  hasAccessToOtherNoteWrite: boolean;
  lastNoteRef?: (node: Element | null) => void;
  firstNoteRef?: (node: Element | null) => void;
}

export const NoteGroup: React.FC<NoteGroupProps> = (props) => {
  const {
    groupName,
    entity,
    notes,
    isFinalStatus,
    hasAccessToWrite,
    hasAccessToOtherNoteDelete,
    hasAccessToOtherNoteWrite,
    lastNoteRef,
    firstNoteRef,
  } = props;
  const [drawerState, setDrawerState] = useState<boolean>(false);
  const [attachmentDrawerState, setAttachmentDrawerState] = useState<boolean>(false);
  const [editData, setEditData] = useState<NoteType | undefined>();
  const [addModalState, setAddModalState] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);
  const [entityData, setEntityData] = useState<Record<string, any[]> | undefined>(undefined);
  const [attachmentData, setAttachmentData] = useState<Record<string, any>[] | undefined>(undefined);

  const onExpand = useCallback((entities?: Record<string, any[]>) => {
    setEntityData(entities);
    setDrawerState((prevState) => !prevState);
  }, []);
  const onAttachmentExpand = useCallback((attachments?: Record<string, any>[]) => {
    setAttachmentData(attachments);
    setAttachmentDrawerState((prevState) => !prevState);
  }, []);
  const onEditNote = useCallback((note?: NoteType) => {
    setAddModalState(true);
    setEditData(note);
  }, []);
  const onAddModalStateChange = useCallback((value: boolean) => {
    setAddModalState(value);
  }, []);
  const onDeleteModalStateChange = useCallback((id?: string) => {
    setDeleteId(id);
  }, []);

  return (
    <Col span={24}>
      <Row gutter={5}>
        <Col>
          <Typography.Text>
            <CalendarIcon />
          </Typography.Text>
        </Col>
        <Col>
          <Typography.Text>{moment(groupName).format('DD.MM.YYYY')}</Typography.Text>
        </Col>
      </Row>
      <Row gutter={[0, 12]}>
        {notes.map((note, idx) => {
          const isLast = idx === notes.length - 1;
          const isFirst = idx === 0;
          return (
            <Col span={24} key={note.id}>
              <Note
                key={note.id}
                note={note}
                entity={entity}
                isFinalStatus={isFinalStatus}
                hasAccessToWrite={hasAccessToWrite}
                hasAccessToOtherNoteWrite={hasAccessToOtherNoteWrite}
                hasAccessToOtherNoteDelete={hasAccessToOtherNoteDelete}
                onExpand={onExpand}
                onAttachmentsExpand={onAttachmentExpand}
                onEditNote={onEditNote}
                onDeleteNote={onDeleteModalStateChange}
                noteRef={isFirst ? firstNoteRef : isLast ? lastNoteRef : undefined}
              />
            </Col>
          );
        })}
      </Row>
      <EntityDrawer drawerState={drawerState} onExpand={onExpand} data={entityData} entity={entity} />
      <AttachmentDrawer drawerState={attachmentDrawerState} onExpand={onAttachmentExpand} data={attachmentData} />
      <NoteAddUpdateModal
        modalState={addModalState}
        onModalStateChange={onAddModalStateChange}
        dataToEdit={editData}
        entity={entity}
      />
      <DeleteModal dataToDelete={deleteId} onDeleteModalClose={onDeleteModalStateChange} openModal={!!deleteId} />
    </Col>
  );
};
